import { Box, Button, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Subsection } from "../utils/Subsection";
import { DetailsItem } from "../utils/DetailsItem";
import secureLocalStorage from "react-secure-storage";
import { hasAdminOrEditorAccess } from "../../helpers/roles";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import SingleDeleteIngredients from "../utils/SingleDeleteIngredients";
import { formatQuantity } from "../../utils/ingredientUtils";

export default function IngredientDetails({ ingredient }) {
  const { t } = useTranslation();
  const storedUser = secureLocalStorage.getItem("userInfo");
  const user = JSON.parse(storedUser);

  const isEmulsifyingAgent = [
    "Emulsifying agent",
    "Emulsifier / emulsion stabilizer",
    "Emulsion stabilizer",
    "Agent emulsifiant/Stabilisateur d'emulsion",
    "Agent emulsifiant",
    "Stabilisateur d'emulsion",
  ].includes(ingredient.technofunctionality);

  const isFoamingAgent = [
    "Agent moussant",
    "Agent moussant/Stabilisateur de mousse",
    "Stabilisateur de mousse",
  ].includes(ingredient.technofunctionality);

  const isFatAgent =
    ingredient.technofunctionality === "Substitut de matière grasse";

  const isGellifantAgent = [
    "Agent gelifiant",
    "Agent gelifiant/Epaississant",
  ].includes(ingredient.technofunctionality);

  const [
    isSingleDeleteIngredientsModalOpen,
    setIsSingleDeleteIngredientsModalOpen,
  ] = useState(false);

  const combinedItem = [ingredient.microorganism].filter(Boolean).join(" - ");

  const displayCombinedItem = combinedItem ? `${combinedItem}` : null;

  const itemStyles = {
    title: {
      fontColor: "green",
      fontWeight: "800",
    },
    ingredient: {
      fontSize: "15px",
      fontWeight: "300",
    },
    marginBottom: "20px",
  };

  let isFirstEfficacy = true;

  const handleCloseSingleDeleteModal = () => {
    setIsSingleDeleteIngredientsModalOpen(!isSingleDeleteIngredientsModalOpen);
  };

  return (
    <>
      <Grid container wrap="nowrap" spacing={2}>
        <Grid item xs md={8}>
          <Typography
            variant="h5"
            fontWeight={"400"}
            marginBottom={"50px"}
            color="green"
          >
            {ingredient.ingredient}
          </Typography>
        </Grid>
        {hasAdminOrEditorAccess(user) && (
          <Grid item xs md={4}>
            {/* <Button
              variant="contained"
              endIcon={<EditIcon />}
              color="success"
              onClick={() => {
                navigate(`/ingredients/${ingredient._id}/edit`, {
                  state: { ingredientData: ingredient },
                });
              }}
            >
              {t("dashboard.ingredientDetails.editBtn")}
            </Button> */}
            <Button
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error"
              onClick={() => {
                console.log({ ingredient });
                // handleDelete(ingredient?._id);
                handleCloseSingleDeleteModal();
              }}
            >
              {t("dashboard.ingredientDetails.deleteBtn")}
            </Button>
          </Grid>
        )}
      </Grid>
      <Subsection text={t("dashboard.ingredientDetails.heading.h1")} />
      <DetailsItem
        styles={itemStyles}
        title={"Ingredient Note"}
        item={ingredient.ingredientNote}
        md={6}
      />
      <DetailsItem
        styles={itemStyles}
        title={"Code"}
        item={ingredient.code}
        md={6}
      />
      <DetailsItem
        styles={itemStyles}
        title={"Technofunctionality"}
        item={ingredient.technofunctionality}
        md={6}
      />
      <DetailsItem
        styles={itemStyles}
        title={"Suppliers"}
        item={ingredient.suppliers}
        md={6}
      />
      <DetailsItem
        styles={itemStyles}
        title={" Source Category"}
        item={ingredient.sourceCategory}
        md={6}
      />
      <DetailsItem
        styles={itemStyles}
        title={"Sector"}
        item={ingredient.sector}
        md={6}
      />
      <DetailsItem
        styles={itemStyles}
        title={"Product Platform"}
        item={ingredient.productPlatform}
        md={6}
      />
      <DetailsItem
        styles={itemStyles}
        title={"Product Type"}
        item={ingredient.productTypeAlpha}
        md={6}
      />

      {/* Physio-Chemical Properties */}
      <Subsection text={t("dashboard.ingredientDetails.heading.h2")} />
      <DetailsItem
        styles={itemStyles}
        title={"Chemical Composition"}
        item={ingredient?.chemicalCompositionStandardized}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Extraction Yield Percentage"}
        item={ingredient.extractionYieldPercentage}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Solubility"}
        item={ingredient.solubilityCategory}
        md={ingredient.solubilityCategory ? 12 : 6}
      />

      <DetailsItem
        styles={itemStyles}
        title={" Polarity"}
        item={ingredient.polarity}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Stability Ph"}
        item={
          isEmulsifyingAgent
            ? ingredient?.phStability
            : ingredient?.stabilityPhStandardized
        }
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Stability Temperature"}
        item={
          isEmulsifyingAgent
            ? ingredient?.heatStability
            : ingredient.stabilityTemperatureStandardized
        }
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Stability Light"}
        item={ingredient.stabilityLight}
        md={6}
      />

      {/* Quantities of Ingredients */}

      <Subsection text={t("dashboard.ingredientDetails.heading.h3")} />
      {isGellifantAgent ? (
        <>
          <DetailsItem
            styles={itemStyles}
            title={"Low Quantity"}
            item={formatQuantity(
              ingredient.lowConcentration,
              ingredient.quantityUnits
            )}
            md={6}
          />

          <DetailsItem
            styles={itemStyles}
            title={"High Quantity"}
            item={formatQuantity(
              ingredient.highConcentration,
              ingredient.quantityUnits
            )}
            md={6}
          />

          <DetailsItem
            styles={itemStyles}
            title={"Optimal Quantity"}
            item={formatQuantity(
              ingredient.optimalConcentration,
              ingredient.quantityUnits
            )}
            md={6}
          />
        </>
      ) : (
        <>
          <DetailsItem
            styles={itemStyles}
            title={"Low Quantity"}
            item={formatQuantity(
              ingredient.lowQuantity,
              ingredient.quantityUnits
            )}
            md={6}
          />

          <DetailsItem
            styles={itemStyles}
            title={"High Quantity"}
            item={formatQuantity(
              ingredient.highQuantity,
              ingredient.quantityUnits
            )}
            md={6}
          />

          <DetailsItem
            styles={itemStyles}
            title={"Optimal Quantity"}
            item={formatQuantity(
              ingredient.optimalConcentration,
              ingredient.quantityUnits
            )}
            md={6}
          />
        </>
      )}

      {/* Effectiveness */}
      <Subsection
        text={t("dashboard.ingredientDetails.heading.h4")}
        item={ingredient.technofunctionality}
        displayText={displayCombinedItem}
      />
      {/* <Subsection
        text={t("dashboard.ingredientDetails.heading.h4")}
        item={ingredient.technofunctionality}
      /> */}

      {Array.from({ length: 19 }, (_, index) => {
        const number = index + 1;

        const effParameterTitle = isFirstEfficacy ? "Eff Parameter" : "";
        const efficacyTitle = isFirstEfficacy ? "Efficacy" : "";
        const efficacyLevelTitle = isFirstEfficacy ? "Efficacy Level" : "";
        const efficacyRangeAmountTitle =
          isFirstEfficacy && isFoamingAgent ? "Efficacy Range/Amount" : "";
        const efficacyAmountTile =
          isFirstEfficacy && isFoamingAgent ? "Efficacy Amount" : "";

        if (
          !ingredient[`effParameter${number}`] &&
          !ingredient[`efficacy${number}`] &&
          !ingredient[`efficacy${number}Classification`]
        )
          return null;

        if (
          isFoamingAgent &&
          !ingredient[`effParameter${number}`] &&
          !ingredient[`efficacy${number}`] &&
          !ingredient[`efficacy${number}Classification`] &&
          !ingredient[`efficacy${number}RangeAmount`] &&
          !ingredient[`efficacy${number}Amount`]
        )
          return null;

        return (
          <>
            <DetailsItem
              key={`effParameter${number}`}
              styles={itemStyles}
              title={effParameterTitle}
              item={ingredient[`effParameter${number}`]}
              md={isFoamingAgent ? 2 : 4}
            />
            <DetailsItem
              key={`efficacy${number}`}
              styles={itemStyles}
              title={efficacyTitle}
              item={ingredient[`efficacy${number}`]}
              md={isFoamingAgent ? 2 : 4}
            />
            {/* <DetailsItem
              key={`efficacy${number}Classification`}
              styles={itemStyles}
              title={efficacyLevelTitle}
              item={ingredient[`efficacy${number}Classification`]}
              md={isFoamingAgent ? 2 : 4}
            /> */}

            {isFatAgent && (
              <DetailsItem
                key={`efficacity${number}Level`}
                styles={itemStyles}
                title={efficacyLevelTitle}
                item={ingredient[`efficacity${number}Level`]}
                md={isFoamingAgent ? 2 : 4}
              />
            )}

            {isFoamingAgent ? (
              <>
                <DetailsItem
                  key={`efficacy${number}Classification`}
                  styles={itemStyles}
                  title={efficacyLevelTitle}
                  item={ingredient[`efficacy${number}Classification`]}
                  md={isFoamingAgent ? 2 : 4}
                />

                <DetailsItem
                  key={`efficacy${number}Amount`}
                  styles={itemStyles}
                  title={efficacyAmountTile}
                  item={parseFloat(
                    ingredient[`efficacy${number}Amount`]
                  ).toFixed(2)}
                  md={2}
                />

                <DetailsItem
                  key={`efficacy${number}RangeAmount`}
                  styles={itemStyles}
                  title={efficacyRangeAmountTitle}
                  item={parseFloat(
                    ingredient[`efficacy${number}RangeAmount`]
                  ).toFixed(2)}
                  md={2}
                />
              </>
            ) : (
              <DetailsItem
                key={`efficacy${number}Level`}
                styles={itemStyles}
                title={efficacyLevelTitle}
                item={ingredient[`efficacy${number}Level`]}
                md={isFoamingAgent ? 2 : 4}
              />
            )}

            {/* {isFoamingAgent && (
              <DetailsItem
                key={`efficacy${number}Amount`}
                styles={itemStyles}
                title={efficacyAmountTile}
                item={parseFloat(ingredient[`efficacy${number}Amount`]).toFixed(
                  2
                )}
                md={2}
              />
            )}

            {isFoamingAgent && (
              <DetailsItem
                key={`efficacy${number}RangeAmount`}
                styles={itemStyles}
                title={efficacyRangeAmountTitle}
                item={parseFloat(
                  ingredient[`efficacy${number}RangeAmount`]
                ).toFixed(2)}
                md={2}
              />
            )} */}
            <hr
              style={{
                border: "none",
                borderTop: "2px dashed #ccc",
                width: "100%",
              }}
            />

            {(isFirstEfficacy = false)}
          </>
        );
      })}

      {/* Other Information */}
      <Subsection text={t("dashboard.ingredientDetails.heading.h5")} />
      <DetailsItem
        styles={itemStyles}
        title={"Effect On Taste"}
        item={ingredient.effectOnTaste}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Phase"}
        item={ingredient.phase}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Other Technofunctionality"}
        item={ingredient.otherTechnofunctionality}
        md={6}
      />

      {/* References */}
      <Subsection text={t("dashboard.ingredientDetails.heading.h6")} />
      <DetailsItem
        styles={itemStyles}
        title={"First Author"}
        item={ingredient.firstAuthor}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Year"}
        item={ingredient.year}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Journal"}
        item={ingredient.journal}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Volume And Issue"}
        item={ingredient.volumeAndIssue}
        md={6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Link"}
        item={ingredient.link}
        md={ingredient.link ? 12 : 6}
        isLink={true}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Authors"}
        item={ingredient.authors}
        md={ingredient.authors ? 12 : 6}
      />

      <DetailsItem
        styles={itemStyles}
        title={"Title"}
        item={ingredient.title}
        md={ingredient.title ? 12 : 6}
      />

      <DetailsItem
        styles={itemStyles}
        title={" Abstract"}
        item={ingredient.abstract}
        md={ingredient.abstract ? 12 : 6}
      />

      <SingleDeleteIngredients
        open={isSingleDeleteIngredientsModalOpen}
        onClose={handleCloseSingleDeleteModal}
        id={ingredient?._id}
      />
    </>
  );
}
